import { useId, useState } from 'react';
import type { Balance } from '@urbanrats/contracts';
import type { ClientGateways } from '../gateway/index.ts';
import { LOCALES, useI18n, type LocaleCode } from '../i18n/index.ts';
import { fmtAtomic, fmtDateTime, fmtMoney } from '../lib/format.ts';
import { REGION_SUGGESTIONS, useDisplay } from '../lib/fx.ts';
import { approxConvert } from '@urbanrats/domain';
import { Approx } from './Approx.tsx';

interface Props {
  gw: ClientGateways;
  balance: Balance | null;
  nowMs: number;
  setLocale: (l: LocaleCode) => void;
}

/**
 * Wallet currency (from the operator launch, read-only), exact balance, approximate display currency with
 * its quote, and a region that only suggests language/display. Deposits and withdrawals are the operator's.
 */
export function WalletPanel({ gw, balance, nowMs, setLocale }: Props) {
  const { t, intl } = useI18n();
  const { prefs, setPrefs } = useDisplay();
  const ids = { display: useId(), region: useId(), cur: useId() };
  const [newCur, setNewCur] = useState(gw.demoSession?.currencies.find((c) => c !== gw.currency.code) ?? '');
  const [msg, setMsg] = useState<string | null>(null);
  const c = gw.currency;
  const quoteInfo = (() => {
    if (!prefs.displayCurrency || prefs.displayCurrency === c.code || !balance) return null;
    const target = gw.fx.displayCurrencies.find((d) => d.code === prefs.displayCurrency);
    if (!target) return null;
    const r = approxConvert(balance.balance, { currency: target.code, scale: target.scale }, gw.fx.quotes(), nowMs);
    if (r.kind === 'NO_QUOTE') return t('wallet.quoteNone');
    if (r.kind === 'STALE') return t('wallet.quoteStale');
    return t('wallet.quote', { rateId: r.quote.rateId, provider: r.quote.provider, asOf: fmtDateTime(r.quote.asOf, intl), ttl: Math.round(r.quote.ttlMs / 1000) });
  })();
  const suggestion = prefs.region ? REGION_SUGGESTIONS[prefs.region] : undefined;

  return (
    <div className="wallet" data-testid="wallet-panel">
      <section>
        <h3>{t('wallet.currency')}</h3>
        <p className="wallet-cur" data-testid="wallet-currency">
          <strong>{c.code}</strong> <span className="chip tiny">{c.kind}</span>
        </p>
        <p className="note">{t('wallet.fromOperator')}</p>
        <p className="note mono">{t('wallet.asset', { asset: c.assetId, network: c.network ?? '—', scale: c.scale, version: c.catalogueVersion })}</p>
        {c.demoOnly && <p className="note">{t('wallet.demoCatalogue')}</p>}
        <p>
          {t('wallet.balance')}: <strong className="tabular" data-testid="wallet-balance">{balance ? fmtMoney(balance.balance, intl) : '—'}</strong>{' '}
          {balance && <Approx money={balance.balance} />}
        </p>
        <p className="note">
          {t('wallet.limits', { min: fmtAtomic(BigInt(gw.stakeLimits.minAtomic), c.scale, c.code, intl), max: fmtAtomic(BigInt(gw.stakeLimits.maxAtomic), c.scale, c.code, intl) })}
        </p>
        <p className="note">{t('wallet.cashier')}</p>
      </section>

      <section>
        <label htmlFor={ids.display}>{t('wallet.display')}</label>
        <select id={ids.display} value={prefs.displayCurrency ?? ''} onChange={(e) => setPrefs({ ...prefs, displayCurrency: e.target.value || null })} data-testid="display-currency">
          <option value="">{t('wallet.displayOff')}</option>
          {gw.fx.displayCurrencies
            .filter((d) => d.code !== c.code)
            .map((d) => (
              <option key={d.code} value={d.code}>
                {d.code}
              </option>
            ))}
        </select>
        <p className="note">{t('wallet.displayNote')}</p>
        {quoteInfo && (
          <p className="note" data-testid="quote-info">
            {quoteInfo}
          </p>
        )}
      </section>

      <section>
        <label htmlFor={ids.region}>{t('wallet.region')}</label>
        <select id={ids.region} value={prefs.region ?? ''} onChange={(e) => setPrefs({ ...prefs, region: e.target.value || null })} data-testid="region">
          <option value="">{t('wallet.regionNone')}</option>
          {Object.keys(REGION_SUGGESTIONS).map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <p className="note">{t('wallet.regionNote')}</p>
        {suggestion && (
          <button
            type="button"
            className="ghost-btn bordered"
            data-testid="apply-region"
            onClick={() => {
              setLocale(suggestion.locale);
              setPrefs({ ...prefs, displayCurrency: suggestion.display === c.code ? null : suggestion.display });
            }}
          >
            {t('wallet.regionApply', { locale: LOCALES.find((l) => l.code === suggestion.locale)?.label ?? suggestion.locale, display: suggestion.display ?? t('wallet.displayOff') })}
          </button>
        )}
      </section>

      {gw.demoSession && (
        <section className="reset">
          <h3>{t('wallet.newSession')}</h3>
          <p className="note">{t('wallet.newSessionHint')}</p>
          <label htmlFor={ids.cur} className="sr-only">
            {t('wallet.currency')}
          </label>
          <div className="dev-row">
            <select id={ids.cur} value={newCur} onChange={(e) => setNewCur(e.target.value)} data-testid="new-session-currency">
              {gw.demoSession.currencies
                .filter((x) => x !== c.code)
                .map((x) => (
                  <option key={x} value={x}>
                    {x}
                  </option>
                ))}
            </select>
            <button
              type="button"
              className="ghost-btn bordered"
              data-testid="new-session"
              onClick={() => {
                const r = gw.demoSession!.start({ currency: newCur, country: gw.session.countryHint });
                if (!r.ok) setMsg(t('wallet.newSessionBlocked'));
              }}
            >
              {t('wallet.start', { currency: newCur })}
            </button>
          </div>
          {msg && (
            <p role="alert" className="field-error">
              {msg}
            </p>
          )}
        </section>
      )}
    </div>
  );
}
